import { useState, FC, Fragment } from "react";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import { useHotkeys } from "react-hotkeys-hook";
import IconButton from "@components/common/IconButton";
import Alert, { AlertState } from "@components/common/Alert";
import { Dialog, Transition } from "@headlessui/react";
import OtpInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import { ReactComponent as LoadingSpinner } from "@images/loading_spinner.svg";
import { OBPersonaAnswerKey } from "@models/Enums";
import house from "@images/onb_let_4.png";
import newspaper from "@images/onb_let_6.png";
import mug from "@images/onb_let_7.png";
import graph from "@images/onb_let_8.png";
import bullseye from "@images/bullseye.png";

interface OnboardingGoalModalProps {
	isShow: boolean;
    setIsShow: (isShow: boolean) => void;
    selectedAnswer: string | undefined;
}   

const OnboardingGoalModal: FC<OnboardingGoalModalProps> = ({ isShow, setIsShow, selectedAnswer }) => {
	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});
	const navigate = useNavigate();

    const getGoalTitle = () => {
        switch (selectedAnswer) {
            case OBPersonaAnswerKey.onePlace:
                return "All your newsletters.\nOne calm, focused space.";
            case OBPersonaAnswerKey.betterReading:
                return "Your newsletters deserve better than email";
            case OBPersonaAnswerKey.keepInboxClean:
                return "60% less inbox clutter.\n3x more focus.";
            case OBPersonaAnswerKey.organiseNewsletters:
                return "From newsletter chaos to\nreading bliss";
            default:
                return "Whatever your goal, Meco helps you read on your terms";
        }
    }

    const getGoalDescription = () => {
        switch (selectedAnswer) {
            case OBPersonaAnswerKey.onePlace:
                return "Meco consolidates your newsletters into one dedicated space, so you read more and get more out of what you subscribe to.";
            case OBPersonaAnswerKey.betterReading:
                return "Stop treating great content like just another email. Meco gives your newsletters a beautiful, focused space with AI summaries for busy days - so you actually read them.";
            case OBPersonaAnswerKey.keepInboxClean:
                return "Meco moves newsletters out of your inbox, so you stop wasting time sorting and start enjoying a calmer, more focused email experience.";
            case OBPersonaAnswerKey.organiseNewsletters:
                return "No more hunting through your inbox for what you wanted to read. Meco automatically organizes your newsletters, so you can find and read everything in seconds.";
            default:
                return "Meco creates a focused, flexible space for your newsletters so you can manage your reading the way that works best for you.";
        }
    }

    const getGoalImage = () => {
        switch (selectedAnswer) {
            case OBPersonaAnswerKey.onePlace:
                return <img src={house} className="w-[150px] m-auto object-cover" />
            case OBPersonaAnswerKey.betterReading:
                return <img src={mug} className="w-[150px] m-auto object-cover" />
            case OBPersonaAnswerKey.keepInboxClean:
                return <img src={graph} className="w-[150px] m-auto object-cover" />
            case OBPersonaAnswerKey.organiseNewsletters:
                return <img src={newspaper} className="w-[150px] m-auto object-cover" />
            default:
                return <img src={bullseye} className="w-[150px] m-auto object-cover" />
        }
    }

	return (
		<>
			<Alert alertState={alertState} setAlertState={setAlertState} />
			<Transition as={Fragment} show={isShow}>
				<Dialog onClose={() => setIsShow(false)} className="relative z-40">
					<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
						<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
					</Transition.Child>
					<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
						<Transition.Child enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
							<Dialog.Panel className="max-w-[400px] transform overflow-hidden rounded-2xl bg-white border border-black/10 text-center align-middle shadow-xl transition-all p-4">
								<div className="flex flex-col gap-2">
                                    {getGoalImage()}
									<div className="text-brand-blue font-black font-primary text-xl whitespace-pre-line">{getGoalTitle()}</div>
									<div className="text-black/50 font-regular font-primary text-sm">{getGoalDescription()}</div>
                                    <button className={`opacity-100 hover:bg-success-green/80 text-white font-medium font-primary text-base focus:outline-none rounded-full p-2 px-3 py-3 items-center bg-success-green transition ease-in-out whitespace-nowrap mt-3`} onClick={() => navigate("/get-started/interests")}>Got it 🎈</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		</>
	);
};

export default OnboardingGoalModal;